import { useMemo } from "react";
import {
  MessageCircle,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  ArrowRight,
  Tag,
  Users,
  Clock,
  CalendarClock,
  Send,
  UserPlus,
  Building2,
  Target,
} from "lucide-react";
import { cn } from "../../../../utils/utils";
import { useBotStats } from "../../hooks/useBotStats";
import { useBotLogs } from "../../hooks/useBotLogs";
import { getStatusInfo } from "../../utils/statusLabels";
import { formatRelative } from "../../utils/formatRelative";
import {
  isTagEvent,
  isGroupEvent,
} from "../../utils/eventType";

const TARGET_LABELS = {
  all: "Tất cả",
  group: "Nhóm",
  user: "User",
};

const SCHEDULE_LABELS = {
  immediate: "Gửi ngay khi trigger",
  delay: "Sau khoảng thời gian",
  specific_time: "Vào giờ cụ thể",
};

const DELAY_UNIT_LABELS = {
  seconds: "giây",
  minutes: "phút",
  hours: "giờ",
  days: "ngày",
};

const sevenDaysAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString();
};

const BotOverviewTab = ({ bot, eventTypes, shopsList, onSwitchTab }) => {
  const dateFrom = useMemo(sevenDaysAgo, []);
  const { stats, loading: statsLoading } = useBotStats(bot.id, { dateFrom });
  const { rows: recentLogs, loading: logsLoading } = useBotLogs(bot.id, {
    dateFrom,
  });

  const successCount = stats.sent + stats.received + stats.seen;
  const successRate = stats.total > 0
    ? Math.round((successCount / stats.total) * 100)
    : null;
  const inFlightCount = stats.pending + stats.scheduled + stats.sending;
  const failCount = stats.failed + stats.dead;

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* KPI cards */}
      <section>
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          Hiệu suất 7 ngày qua
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            icon={MessageCircle}
            label="Tổng tin nhắn"
            value={stats.total}
            tone="indigo"
            loading={statsLoading}
            sub="trong 7 ngày qua"
          />
          <KpiCard
            icon={CheckCircle2}
            label="Thành công"
            value={successCount}
            tone="emerald"
            loading={statsLoading}
            sub={successRate != null ? `Tỉ lệ ${successRate}%` : "—"}
          />
          <KpiCard
            icon={Loader2}
            label="Đang chờ / gửi"
            value={inFlightCount}
            tone="blue"
            loading={statsLoading}
            sub={`${stats.pending} chờ · ${stats.scheduled} lên lịch · ${stats.sending} đang gửi`}
            animate={inFlightCount > 0}
          />
          <KpiCard
            icon={AlertTriangle}
            label="Thất bại"
            value={failCount}
            tone={failCount > 0 ? "rose" : "gray"}
            loading={statsLoading}
            sub={
              failCount > 0
                ? `${stats.failed} retry · ${stats.dead} dừng`
                : "Chưa có lỗi"
            }
            actionLabel={failCount > 0 ? "Xem chi tiết" : null}
            onAction={() => onSwitchTab("messages")}
          />
        </div>
      </section>

      {/* Bot config summary */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              Cấu hình bot
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Xem nhanh các thông số chính. Chỉnh sửa ở tab &quot;Cài đặt&quot;.
            </p>
          </div>
          <button
            type="button"
            onClick={() => onSwitchTab("config")}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1"
          >
            Chỉnh sửa
            <ArrowRight size={12} />
          </button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <ConfigField
            icon={Tag}
            label="Loại sự kiện"
            value={
              eventTypes.find((e) => e.action === bot.eventType)?.actionName ||
              bot.eventType ||
              "—"
            }
          />
          <ConfigField
            icon={Target}
            label="Điều kiện"
            value={renderCondition(bot)}
          />
          <ConfigField
            icon={Building2}
            label="Tài khoản Zalo"
            value={renderShops(bot.shopIds, shopsList)}
          />
          <ConfigField
            icon={Users}
            label="Đối tượng"
            value={TARGET_LABELS[bot.targetType] || bot.targetType || "—"}
          />
          <ConfigField
            icon={CalendarClock}
            label="Lịch trình"
            value={renderSchedule(bot)}
          />
          {(bot.eventType === "join_group" ||
            bot.eventType === "leave_group") && (
            <ConfigField
              icon={UserPlus}
              label="Tự động kết bạn"
              value={bot.isAddFriend ? "Có" : "Không"}
            />
          )}
          <div className="md:col-span-2">
            <ConfigField
              icon={Send}
              label="Nội dung gửi"
              value={
                bot.contentReply ? (
                  <div className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-3 bg-gray-50 rounded-md p-2 border border-gray-100">
                    {bot.contentReply}
                  </div>
                ) : (
                  <span className="italic text-gray-400">(Chưa có nội dung)</span>
                )
              }
              wide
            />
          </div>
        </div>
      </section>

      {/* Recent messages preview */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              Tin nhắn gần đây
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              5 tin nhắn mới nhất bot đã gửi
            </p>
          </div>
          <button
            type="button"
            onClick={() => onSwitchTab("messages")}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1"
          >
            Xem tất cả
            <ArrowRight size={12} />
          </button>
        </div>
        <div className="divide-y divide-gray-100">
          {logsLoading ? (
            <RecentSkeleton />
          ) : recentLogs.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-500">
              Bot chưa gửi tin nhắn nào trong 7 ngày qua.
            </div>
          ) : (
            recentLogs.slice(0, 5).map((log) => (
              <RecentRow key={log.id} log={log} />
            ))
          )}
        </div>
      </section>
    </div>
  );
};

const KpiCard = ({
  icon: Icon,
  label,
  value,
  sub,
  tone = "indigo",
  loading,
  animate,
  actionLabel,
  onAction,
}) => {
  const toneStyles = {
    indigo: "bg-indigo-50 text-indigo-600 ring-indigo-100",
    emerald: "bg-emerald-50 text-emerald-600 ring-emerald-100",
    blue: "bg-blue-50 text-blue-600 ring-blue-100",
    rose: "bg-rose-50 text-rose-600 ring-rose-100",
    gray: "bg-gray-100 text-gray-500 ring-gray-200",
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "w-7 h-7 rounded-md inline-flex items-center justify-center ring-1",
            toneStyles[tone],
          )}
        >
          <Icon size={14} className={animate ? "animate-spin" : ""} />
        </span>
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <div className="text-2xl font-semibold text-gray-900 tabular-nums">
        {loading ? <span className="inline-block w-12 h-6 bg-gray-100 rounded animate-pulse" /> : value.toLocaleString("vi-VN")}
      </div>
      <div className="text-[11px] text-gray-500 flex items-center justify-between gap-2">
        <span className="truncate">{sub}</span>
        {actionLabel && (
          <button
            type="button"
            onClick={onAction}
            className="text-[11px] font-medium text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-0.5 shrink-0"
          >
            {actionLabel}
            <ArrowRight size={10} />
          </button>
        )}
      </div>
    </div>
  );
};

const ConfigField = ({ icon: Icon, label, value, wide }) => (
  <div className={cn("flex items-start gap-2.5", wide ? "col-span-full" : "")}>
    <span className="w-7 h-7 mt-0.5 rounded-md bg-gray-100 inline-flex items-center justify-center shrink-0 text-gray-500">
      <Icon size={14} />
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-[11px] uppercase tracking-wider text-gray-400 font-medium">
        {label}
      </div>
      <div className="text-sm text-gray-900 mt-0.5">{value}</div>
    </div>
  </div>
);

const RecentRow = ({ log }) => {
  const status = getStatusInfo(log.status);
  const StatusIcon = status.icon;
  const initial = (log.contactName || log.zaloName || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors">
      <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 inline-flex items-center justify-center text-sm font-semibold shrink-0">
        {initial}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 truncate">
            {log.contactName || log.zaloName || "Khách lạ"}
          </span>
          <span
            className={cn(
              "inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ring-1",
              status.bg,
              status.color,
              status.ring,
            )}
          >
            <StatusIcon size={10} className={log.status === "sending" ? "animate-spin" : ""} />
            {status.label}
          </span>
        </div>
        <div className="text-xs text-gray-500 truncate mt-0.5">
          {log.botReply || log.contactQuestion || "—"}
        </div>
      </div>
      <div className="text-[11px] text-gray-400 shrink-0 inline-flex items-center gap-1">
        <Clock size={10} />
        {formatRelative(log.dateSent || log.createdAt)}
      </div>
    </div>
  );
};

const RecentSkeleton = () =>
  Array.from({ length: 3 }).map((_, i) => (
    <div key={i} className="px-5 py-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-gray-100 animate-pulse shrink-0" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3 w-1/3 bg-gray-100 rounded animate-pulse" />
        <div className="h-2.5 w-2/3 bg-gray-100 rounded animate-pulse" />
      </div>
    </div>
  ));

const renderCondition = (bot) => {
  if (!bot.conditionValue || bot.conditionValue.length === 0) {
    if (isTagEvent(bot.eventType)) return <span className="italic text-gray-400">Chưa chọn tag</span>;
    if (isGroupEvent(bot.eventType)) return <span className="italic text-gray-400">Chưa chọn nhóm</span>;
    return <span className="italic text-gray-400">Không yêu cầu</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {bot.conditionValue.slice(0, 6).map((v, i) => (
        <span
          key={i}
          className="inline-flex text-[11px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100"
        >
          {v}
        </span>
      ))}
      {bot.conditionValue.length > 6 && (
        <span className="text-[11px] text-gray-500">
          +{bot.conditionValue.length - 6}
        </span>
      )}
    </div>
  );
};

const renderShops = (shopIds, shopsList) => {
  if (!shopIds || shopIds.length === 0)
    return <span className="italic text-gray-400">Chưa chọn</span>;
  const names = shopIds
    .map((id) => shopsList.find((s) => s.id === id)?.name || id)
    .slice(0, 3);
  return (
    <span>
      {names.join(", ")}
      {shopIds.length > 3 && (
        <span className="text-gray-500"> +{shopIds.length - 3}</span>
      )}
    </span>
  );
};

const renderSchedule = (bot) => {
  const base = SCHEDULE_LABELS[bot.scheduleType] || bot.scheduleType;
  if (bot.scheduleType === "delay" && bot.delayValue != null) {
    return `${base} (${bot.delayValue} ${DELAY_UNIT_LABELS[bot.delayUnit] || bot.delayUnit})`;
  }
  if (bot.scheduleType === "specific_time" && bot.specificTime) {
    return `${base} (${String(bot.specificTime).slice(0, 5)})`;
  }
  return base;
};

export default BotOverviewTab;
